/**
 * API service for the Dola Debt Portal.
 * Handles all communication with the backend stub API.
 * Base URL is configurable via REACT_APP_API_URL environment variable.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/portal';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  const response = await fetch(url, config);

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: response.statusText };
    }
    throw new ApiError(
      errorData?.error || `Request failed with status ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json();
}

/**
 * Resolve a short token to its LoanApplicationId.
 * Called when a client clicks their SMS link.
 * @param {string} token - The short token from the URL
 * @returns {Promise<{token: string, loanApplicationId: string, isValid: boolean}>}
 */
export async function resolveToken(token) {
  return request(`/link/resolve/${encodeURIComponent(token)}`);
}

/**
 * Fetch the debt summary (all accounts) for a client using their token.
 * @param {string} token - The short token
 * @returns {Promise<Object>} Debt summary with account list
 */
export async function getDebtSummary(token) {
  return request(`/accounts/debt-summary?token=${encodeURIComponent(token)}`);
}

/**
 * Fetch all accounts for a token.
 * @param {string} token - The short token
 * @returns {Promise<Array>} List of account summaries
 */
export async function getAccounts(token) {
  return request(`/accounts?token=${encodeURIComponent(token)}`);
}

/**
 * Fetch detail for a specific account.
 * @param {string} token - The short token
 * @param {string} loanApplicationId - The loan application ID
 * @returns {Promise<Object>} Account detail
 */
export async function getAccountDetail(token, loanApplicationId) {
  return request(`/accounts/${encodeURIComponent(loanApplicationId)}?token=${encodeURIComponent(token)}`);
}

/**
 * Verify client identity by submitting last 4 digits of their ID number.
 * Creates a session on success.
 * @param {string} token - The short link token
 * @param {string} idLastFourDigits - Last 4 digits of SA ID number
 * @returns {Promise<{success: boolean, sessionToken?: string, clientName?: string, message: string, attemptsRemaining?: number, isLockedOut?: boolean}>}
 */
export async function verifyIdentity(token, idLastFourDigits) {
  return request('/session/verify', {
    method: 'POST',
    body: JSON.stringify({ token, idLastFourDigits }),
  });
}

/**
 * Validate the current session token.
 * @param {string} sessionToken - The session token to validate
 * @returns {Promise<Object>} Session context with client details
 */
export async function validateSession(sessionToken) {
  return request('/session/status', {
    headers: {
      'X-Session-Token': sessionToken,
    },
  });
}

/**
 * End the current session (logout).
 * @param {string} sessionToken - The session token to invalidate
 * @returns {Promise<Object>}
 */
export async function logout(sessionToken) {
  return request('/session/logout', {
    method: 'POST',
    headers: {
      'X-Session-Token': sessionToken,
    },
  });
}

/**
 * Create a request function that automatically includes the session token.
 * @param {string} sessionToken - The session token
 * @returns {Function} A request function with session header pre-set
 */
export function createAuthenticatedRequest(sessionToken) {
  return (path, options = {}) => {
    return request(path, {
      ...options,
      headers: {
        ...options.headers,
        'X-Session-Token': sessionToken,
      },
    });
  };
}

/**
 * Fetch the pre-approved settlement quote for a specific loan.
 * Returns settlement amount, discount, savings, and Netcash payment URL.
 * @param {string} loanApplicationId - The loan application ID
 * @returns {Promise<Object>} Settlement quote with Netcash payment URL
 */
export async function getSettlementQuote(loanApplicationId) {
  return request(`/settlement/quote/${encodeURIComponent(loanApplicationId)}`);
}

/**
 * Submit a settlement offer for an account.
 * @param {Object} offerData - The settlement offer payload
 * @returns {Promise<Object>} Settlement offer response
 */
export async function submitSettlementOffer(offerData) {
  return request('/settlement/offer', {
    method: 'POST',
    body: JSON.stringify(offerData),
  });
}

/**
 * Fetch unmasked client details after ID verification.
 * Requires a valid session token.
 * @param {string} token - The short link token
 * @param {string} sessionToken - The verified session token
 * @returns {Promise<{clientFirstName: string, clientLastName: string, clientName: string, email: string, phoneNumber: string, idNumber: string, loanApplicationId: string, isVerified: boolean}>}
 */
export async function getClientDetails(token, sessionToken) {
  return request(`/accounts/client-details?token=${encodeURIComponent(token)}`, {
    headers: {
      'X-Session-Token': sessionToken,
    },
  });
}

/**
 * Re-fetch the debt summary with session token for unmasked data.
 * @param {string} token - The short token
 * @param {string} sessionToken - The verified session token
 * @returns {Promise<Object>} Unmasked debt summary
 */
export async function getUnmaskedDebtSummary(token, sessionToken) {
  return request(`/accounts/debt-summary?token=${encodeURIComponent(token)}`, {
    headers: {
      'X-Session-Token': sessionToken,
    },
  });
}

/**
 * Submit a negotiate terms request.
 * Supports reduced_balance, interest_waiver, fee_reversal, and extended_term types.
 * @param {Object} negotiateData - The negotiation request payload
 * @returns {Promise<Object>} Negotiation response with reference number and comparison
 */
export async function submitNegotiation(negotiateData) {
  return request('/negotiate/terms', {
    method: 'POST',
    body: JSON.stringify(negotiateData),
  });
}

/**
 * Fetch DebiCheck payment arrangement form data including eligibility,
 * available amounts, frequencies, banks, and pre-populated details.
 * @param {string} loanApplicationId - The loan application ID
 * @returns {Promise<Object>} Arrangement data with banks and frequencies
 */
export async function getArrangementData(loanApplicationId) {
  return request(`/payment/arrangement-data/${encodeURIComponent(loanApplicationId)}`);
}

/**
 * Submit a new DebiCheck payment arrangement request.
 * @param {Object} data - { amount, frequency, nextPayDate, bankId?, accountNumber, accountType, email?, phone? }
 * @returns {Promise<Object>} Arrangement response with reference number and DebiCheck message
 */
export async function submitPaymentArrangement(data) {
  return request('/payment/arrangements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Fetch available objection types with descriptions.
 * @returns {Promise<{success: boolean, types: Array<{value: string, label: string}>}>}
 */
export async function getObjectionTypes() {
  return request('/objection/types');
}

/**
 * Submit a formal objection against an account.
 * @param {Object} objectionData - The objection payload
 * @returns {Promise<Object>} Objection response with reference number and status
 */
export async function submitObjection(objectionData) {
  return request('/objection', {
    method: 'POST',
    body: JSON.stringify(objectionData),
  });
}

/**
 * Schedule a callback from Reinvent Debt Solutions or Dola Money.
 * @param {Object} scheduleData - { loanApplicationId, provider, contactNumber, email, preferredDateTime, notes? }
 * @returns {Promise<Object>} Scheduling confirmation with reference number
 */
export async function scheduleContact(scheduleData) {
  return request('/scheduling/contact', {
    method: 'POST',
    body: JSON.stringify(scheduleData),
  });
}

/**
 * Refresh a session using a refresh token.
 * Returns a new session token, refresh token, and expiry.
 * @param {string} refreshToken - The refresh token issued at login
 * @returns {Promise<{success: boolean, sessionToken: string, refreshToken: string, expiresAt: string}>}
 */
export async function refreshSession(refreshToken) {
  return request('/session/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  });
}

/**
 * Get the current mandate/arrangement status for a payment arrangement.
 * Requires a valid session token.
 * @param {string} sessionToken - The session token
 * @returns {Promise<{success: boolean, status: string, message: string}>}
 */
export async function getArrangementStatus(sessionToken) {
  return request('/payment/arrangement-status', {
    headers: {
      'X-Session-Token': sessionToken,
    },
  });
}

/**
 * Check the payment status for a settlement on a loan.
 * @param {string} loanApplicationId - The loan application ID
 * @param {string} [sessionToken] - Optional session token
 * @returns {Promise<{success: boolean, hasSettlement: boolean, status: string, message: string}>}
 */
export async function getSettlementStatus(loanApplicationId, sessionToken) {
  return request(`/settlement/status/${encodeURIComponent(loanApplicationId)}`, {
    headers: sessionToken ? { 'X-Session-Token': sessionToken } : {}
  });
}

export { ApiError };
export default {
  resolveToken,
  getDebtSummary,
  getAccounts,
  getAccountDetail,
  verifyIdentity,
  validateSession,
  logout,
  createAuthenticatedRequest,
  getSettlementQuote,
  submitSettlementOffer,
  submitNegotiation,
  getClientDetails,
  getUnmaskedDebtSummary,
  getArrangementData,
  submitPaymentArrangement,
  getObjectionTypes,
  submitObjection,
  scheduleContact,
  refreshSession,
  getArrangementStatus,
  getSettlementStatus,
};
